"use client";

import { useState, useCallback, useEffect } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import { Separator } from "@/shadcn-components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shadcn-components/ui/tabs";
import { Slider } from "@/shadcn-components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
} from "@/shadcn-components/ui/drawer";
import { ColorPickerModal } from "./components";
import { useChartSettingsStore } from "@/lib/chart-settings";
import { useSetScheme } from "@/lib/chart-settings/hooks";
import type { ChartStyle } from "@/lib/chart-settings/types";

interface ColorsScreenProps {
  onBack: () => void;
}

interface ColorItem {
  id: keyof Omit<ChartStyle, "scheme">;
  label: string;
  color: string;
  category: "chart" | "price" | "trading";
}

export default function ColorsScreen({ onBack }: ColorsScreenProps) {
  const { chartSettings, updateChartSettingsStyle } = useChartSettingsStore();
  const setScheme = useSetScheme();
  const { chartStyle } = chartSettings;
  
  const [selectedColorItem, setSelectedColorItem] = useState<ColorItem | null>(null);
  const [showColorDrawer, setShowColorDrawer] = useState(false);

  // Available color schemes
  const SCHEMES = [
    { value: "blackOnWhite", label: "Black on White" },
    { value: "colorOnWhite", label: "Color on White" },
    { value: "greenOnBlack", label: "Green on Black" },
    { value: "custom", label: "Custom" },
  ];

  // Helper functions for color conversion
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  const rgbToHex = (r: number, g: number, b: number) => {
    return "#" + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    }).join("");
  };

  const handleColorClick = (item: ColorItem) => {
    setSelectedColorItem(item);
    setShowColorDrawer(true);
  };

  const handleColorChange = (color: string) => {
    if (selectedColorItem) {
      updateChartSettingsStyle(selectedColorItem.id, color);
      // When user changes a color manually, set scheme to custom
      updateChartSettingsStyle("scheme", "custom");
      setShowColorDrawer(false);
    }
  };

  const handleSchemeChange = (scheme: string) => {
    // Apply preset colors based on scheme
    if (scheme === "blackOnWhite" || scheme === "greenOnBlack" || scheme === "colorOnWhite") {
      setScheme(scheme);
    } else if (scheme === "custom") {
      // For custom, just update the scheme field without changing colors
      updateChartSettingsStyle("scheme", "custom");
    }
  };

  const COLOR_ITEMS: ColorItem[] = [
    { id: "foreground", label: "Foreground", color: chartStyle.foreground, category: "chart" },
    { id: "grid", label: "Grid", color: chartStyle.grid, category: "chart" },
    { id: "barUp", label: "Bar Up", color: chartStyle.barUp, category: "chart" },
    { id: "barDown", label: "Bar Down", color: chartStyle.barDown, category: "chart" },
    { id: "bullCandle", label: "Bull Candle", color: chartStyle.bullCandle, category: "chart" },
    { id: "bearCandle", label: "Bear Candle", color: chartStyle.bearCandle, category: "chart" },
    { id: "lineChart", label: "Line Chart", color: chartStyle.lineChart, category: "chart" },
    { id: "volumes", label: "Volumes", color: chartStyle.volumes, category: "chart" },
    { id: "bidPriceLine", label: "Bid Price Line", color: chartStyle.bidPriceLine, category: "price" },
    { id: "askPriceLine", label: "Ask Price Line", color: chartStyle.askPriceLine, category: "price" },
    { id: "stopsLevels", label: "Stops Levels", color: chartStyle.stopsLevels, category: "trading" },
  ];

  // Group colors by category
  const colorsByCategory = {
    chart: COLOR_ITEMS.filter(item => item.category === "chart"),
    price: COLOR_ITEMS.filter(item => item.category === "price"),
    trading: COLOR_ITEMS.filter(item => item.category === "trading"),
  };

  const ColorItemComponent = ({ item, index, total }: { item: ColorItem; index: number; total: number }) => (
    <Button
      variant="ghost"
      onClick={() => handleColorClick(item)}
      className={`w-full h-auto p-2 justify-between hover:bg-muted/50 rounded-none
        ${index === 0 ? "rounded-t-lg" : ""}
        ${index === total - 1 ? "rounded-b-lg" : ""}
      `}
    >
      <Label className="text-sm font-medium cursor-pointer">{item.label}</Label>
      <div
        className="w-6 h-6 rounded-full border border-border flex-shrink-0"
        style={{ backgroundColor: item.color }}
      />
    </Button>
  );

  // Spectrum Color Picker Component
  const SpectrumColorPicker = ({ selectedColor, onColorChange }: { selectedColor: string; onColorChange: (color: string) => void }) => {
    const [position, setPosition] = useState({ x: 50, y: 50 });
    
    const handleSpectrumClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      
      setPosition({ x, y });
      
      // Convert position to HSV and then to RGB
      const h = (x / 100) * 360;
      const s = 1 - (y / 100);
      const v = 1;
      
      // HSV to RGB conversion
      const c = v * s;
      const x1 = c * (1 - Math.abs(((h / 60) % 2) - 1));
      const m = v - c;
      
      let r = 0, g = 0, b = 0;
      
      if (h >= 0 && h < 60) {
        r = c; g = x1; b = 0;
      } else if (h >= 60 && h < 120) {
        r = x1; g = c; b = 0;
      } else if (h >= 120 && h < 180) {
        r = 0; g = c; b = x1;
      } else if (h >= 180 && h < 240) {
        r = 0; g = x1; b = c;
      } else if (h >= 240 && h < 300) {
        r = x1; g = 0; b = c;
      } else if (h >= 300 && h < 360) {
        r = c; g = 0; b = x1;
      }
      
      r = Math.round((r + m) * 255);
      g = Math.round((g + m) * 255);
      b = Math.round((b + m) * 255);
      
      const hexColor = rgbToHex(r, g, b);
      onColorChange(hexColor);
    }, [onColorChange]);

    return (
      <div className="space-y-4">
        <div 
          className="relative w-full h-64 rounded-lg cursor-crosshair overflow-hidden"
          style={{
            background: `linear-gradient(to bottom, 
              rgba(255,255,255,1) 0%, 
              rgba(255,255,255,0) 50%, 
              rgba(0,0,0,1) 100%),
              linear-gradient(to right, 
              #ff0000 0%, 
              #ffff00 16.66%, 
              #00ff00 33.33%, 
              #00ffff 50%, 
              #0000ff 66.66%, 
              #ff00ff 83.33%, 
              #ff0000 100%)`
          }}
          onClick={handleSpectrumClick}
        >
          <div
            className="absolute w-4 h-4 border-2 border-white rounded-full transform -translate-x-2 -translate-y-2 shadow-lg"
            style={{
              left: `${position.x}%`,
              top: `${position.y}%`,
              backgroundColor: selectedColor
            }}
          />
        </div>
      </div>
    );
  };

  // RGB Sliders Component
  const RGBSliders = ({ selectedColor, onColorChange }: { selectedColor: string; onColorChange: (color: string) => void }) => {
    const [r, setR] = useState(0);
    const [g, setG] = useState(0);
    const [b, setB] = useState(0);

    // Update local state when selectedColor changes externally
    useEffect(() => {
      const rgb = hexToRgb(selectedColor);
      setR(rgb.r);
      setG(rgb.g);
      setB(rgb.b);
    }, [selectedColor]);

    const updateColor = useCallback((newR: number, newG: number, newB: number) => {
      const hexColor = rgbToHex(newR, newG, newB);
      onColorChange(hexColor);
    }, [onColorChange]);

    const handleRChange = (value: number[]) => {
      const newR = value[0];
      setR(newR);
      updateColor(newR, g, b);
    };

    const handleGChange = (value: number[]) => {
      const newG = value[0];
      setG(newG);
      updateColor(r, newG, b);
    };

    const handleBChange = (value: number[]) => {
      const newB = value[0];
      setB(newB);
      updateColor(r, g, newB);
    };

    return (
      <div className="space-y-6">
        {/* RED Slider */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium">RED</Label>
            <span className="text-sm font-mono text-muted-foreground">{r}</span>
          </div>
          <Slider
            value={[r]}
            onValueChange={handleRChange}
            max={255}
            step={1}
            className="w-full"
          />
        </div>

        {/* GREEN Slider */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium">GREEN</Label>
            <span className="text-sm font-mono text-muted-foreground">{g}</span>
          </div>
          <Slider
            value={[g]}
            onValueChange={handleGChange}
            max={255}
            step={1}
            className="w-full"
          />
        </div>

        {/* BLUE Slider */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium">BLUE</Label>
            <span className="text-sm font-mono text-muted-foreground">{b}</span>
          </div>
          <Slider
            value={[b]}
            onValueChange={handleBChange}
            max={255}
            step={1}
            className="w-full"
          />
        </div>

        {/* Color Preview */}
        <div className="mt-6 p-4 bg-muted/20 rounded-lg">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-lg border border-border"
              style={{ backgroundColor: selectedColor }}
            />
            <div>
              <p className="text-sm font-medium">Display P3 Hex Color #</p>
              <p className="text-lg font-mono">{selectedColor.replace('#', '').toUpperCase()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header according to developer-guidelines */}
      <div className="flex items-center justify-between px-4 py-3 h-14 bg-background border-b border-border">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-10 w-10">
          <FaChevronLeft className="text-blue-400 text-xl" />
        </Button>
        <h1 className="text-xl font-medium text-foreground">Colors</h1>
        {/* Empty div for spacing balance */}
        <div className="w-10"></div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Scheme Section */}
        <div className="px-4 pt-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            COLOR SCHEME
          </h2>
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Scheme</Label>
                <Select value={chartStyle.scheme} onValueChange={handleSchemeChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select scheme" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHEMES.map((scheme) => (
                      <SelectItem key={scheme.value} value={scheme.value}>
                        {scheme.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colors Section with Tabs */}
        <div className="px-4 pb-4">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            CUSTOM COLORS
          </h2>
          
          <Tabs defaultValue="chart" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-3">
              <TabsTrigger value="chart" className="text-sm">Chart</TabsTrigger>
              <TabsTrigger value="price" className="text-sm">Price</TabsTrigger>
              <TabsTrigger value="trading" className="text-sm">Trading</TabsTrigger>
            </TabsList>
            
            <TabsContent value="chart" className="mt-0">
              <Card>
                <CardContent className="p-0">
                  {colorsByCategory.chart.map((item, index) => (
                    <div key={item.id}>
                      <ColorItemComponent 
                        item={item} 
                        index={index} 
                        total={colorsByCategory.chart.length} 
                      />
                      {index < colorsByCategory.chart.length - 1 && <Separator />}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="price" className="mt-0">
              <Card>
                <CardContent className="p-0">
                  {colorsByCategory.price.map((item, index) => (
                    <div key={item.id}>
                      <ColorItemComponent 
                        item={item} 
                        index={index} 
                        total={colorsByCategory.price.length} 
                      />
                      {index < colorsByCategory.price.length - 1 && <Separator />}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="trading" className="mt-0">
              <Card>
                <CardContent className="p-0">
                  {colorsByCategory.trading.map((item, index) => (
                    <div key={item.id}>
                      <ColorItemComponent 
                        item={item} 
                        index={index} 
                        total={colorsByCategory.trading.length} 
                      />
                      {index < colorsByCategory.trading.length - 1 && <Separator />}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Bottom spacing */}
        <div className="h-6"></div>
      </div>

      {/* Color Picker Drawer */}
      <Drawer open={showColorDrawer} onOpenChange={setShowColorDrawer}>
        <DrawerContent>
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-xl font-semibold">
              {selectedColorItem?.label || "Select Color"}
            </DrawerTitle>
            <DrawerDescription className="text-sm text-muted-foreground">
              Choose a color for {selectedColorItem?.label?.toLowerCase()}
            </DrawerDescription>
          </DrawerHeader>
          
          {selectedColorItem && (
            <div className="px-4 pb-6">
              {/* Color Picker Tabs */}
              <Tabs defaultValue="spectrum" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="grid" className="text-sm">Grid</TabsTrigger>
                  <TabsTrigger value="spectrum" className="text-sm">Spectrum</TabsTrigger>
                  <TabsTrigger value="sliders" className="text-sm">Sliders</TabsTrigger>
                </TabsList>
                
                <TabsContent value="grid" className="mt-0">
                  <div className="space-y-4">
                    <div className="grid grid-cols-6 gap-3">
                      {[
                        "#F56565", "#3B82F6", "#10B981", "#EAB308", "#EF4444", "#8B5CF6",
                        "#06B6D4", "#F97316", "#EC4899", "#84CC16", "#6366F1", "#F59E0B",
                        "#000000", "#374151", "#6B7280", "#9CA3AF", "#D1D5DB", "#FFFFFF"
                      ].map((color) => (
                        <Button
                          key={color}
                          variant="outline"
                          className="w-12 h-12 p-0 border-2 hover:border-primary"
                          style={{ backgroundColor: color }}
                          onClick={() => handleColorChange(color)}
                        />
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="spectrum" className="mt-0">
                  <SpectrumColorPicker 
                    selectedColor={selectedColorItem.color}
                    onColorChange={handleColorChange}
                  />
                </TabsContent>
                
                <TabsContent value="sliders" className="mt-0">
                  <RGBSliders 
                    selectedColor={selectedColorItem.color}
                    onColorChange={handleColorChange}
                  />
                </TabsContent>
              </Tabs>

              <Separator className="my-6" />
              
              <DrawerClose asChild>
                <Button variant="outline" className="w-full">
                  Done
                </Button>
              </DrawerClose>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
